import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { useTranslate } from '@growtherapy/i18n';

import { TrackingEvents, sendLoggingEvents } from '../../events';
import { useTheme } from '../../hooks/useTheme';
import { Theme } from '../../state/theme';
import {
  type BackgroundSettings,
  BackgroundType,
  LocalMediaStatus,
  hasBackgroundProcessorFailureAtom,
  localVideoContextAtom,
  useStoredAudioVideoSettings,
  useSwitchBackground,
} from '../../video-sdk';
import { permissionDeniedAtom } from '../../video-sdk/twilio';
import { BackgroundSettingsTab } from './BackgroundSettingsTab';

export function BackgroundSettingsTabWrapper() {
  const t = useTranslate();
  const { videoIsBlocked } = useAtomValue(permissionDeniedAtom);
  const hasBackgroundProcessorFailure = useAtomValue(hasBackgroundProcessorFailureAtom);
  const { delegator: localVideoDelegator, status: localVideoStatus } = useAtomValue(localVideoContextAtom);
  const isVideoEnabled = localVideoDelegator && localVideoStatus === LocalMediaStatus.READY;
  const { storedAudioVideoSettings } = useStoredAudioVideoSettings();
  const helpText = videoIsBlocked
    ? t('telehealth-core:settings.cameraIsNotEnabledAllowAccessFromSiteSettings')
    : t('telehealth-core:settings.cameraIsTurnedOffSelectingBackgroundWillTurnOn');

  const switchBackground = useSwitchBackground();
  const { theme } = useTheme();

  const setBackgroundSettings = useCallback(
    (backgroundSettings: BackgroundSettings) => {
      if (backgroundSettings.backgroundType === BackgroundType.IMAGE) {
        sendLoggingEvents(TrackingEvents.ANY_VIRTUAL_BACKGROUND_USE);
      }
      switchBackground(backgroundSettings);
    },
    [switchBackground],
  );

  return (
    <BackgroundSettingsTab
      setBackgroundSettings={setBackgroundSettings}
      isDarkTheme={theme === Theme.DARK}
      isMirrored={storedAudioVideoSettings?.videoIsMirrored ?? false}
      backgroundsSelectionIsDisabled={videoIsBlocked}
      selectedBackground={storedAudioVideoSettings?.backgroundType}
      selectedBackgroundIndex={storedAudioVideoSettings?.backgroundIndex}
      {...(!isVideoEnabled && {
        helpText,
      })}
      hasBackgroundProcessorFailure={hasBackgroundProcessorFailure}
    />
  );
}
